/**
 * Alex Perry Gorgias chat facade.
 *
 * The launcher button is server-rendered in snippets/ap-gorgias.liquid; the
 * Gorgias bundle is only injected when the visitor clicks it (chat campaigns
 * are not in use — see the snippet header). After the widget loads, this
 * module squares the widget UI, applies the brand font and icons inside the
 * widget iframes, and wires Escape/click-away close. The default Gorgias
 * launcher is hidden by static CSS in the snippet's stylesheet, not here.
 *
 * Gorgias DOM hooks this depends on (re-audit after every widget update — the
 * 2026-07 update invalidated half the previous set, and the class names are
 * build-specific Emotion hashes, so none of them are safe to key off):
 *
 *   #conversation                       conversation view (absent on the home
 *                                       screen — used to scope radii)
 *   #conversation-floating-header       header band holding back + close
 *   #privacy-policy-title               sr-only title inside the [role=note]
 *                                       privacy disclaimer
 *   [role="region"]                     scrolling content column
 *   [role="alert"]                      system message strip
 *   div[class*="widget-"]               widget root (widget-xs/sm/md/lg)
 *   <canvas> inside the widget root     Gorgias's texture layers. Verify any
 *                                       background work by sampling rendered
 *                                       pixels, not getComputedStyle — canvas
 *                                       paint is invisible to computed styles
 *                                       and to elementsFromPoint
 *   #chat-message-input                 the composer textarea, which exists
 *                                       only once a conversation is open
 *   iframe#chat-window                  the widget itself; iframe#chat-input
 *                                       is created empty and stays hidden
 *   aria-label*="Close"                 close controls; the widget follows the
 *                                       store language, so this is English-only
 *                                       — revisit if the store ever localizes
 */

const BUTTON_ID = 'ap-gorgias-chat-button';
const CONFIG_ID = 'ap-gorgias-config';
const ICONS_TEMPLATE_ID = 'ap-gorgias-icons';
const BUNDLE_SCRIPT_ID = 'gorgias-chat-widget-install-v3';
const CHAT_WINDOW_SELECTOR = '#gorgias-chat-container iframe#chat-window';
const CHAT_INPUT_SELECTOR = '#gorgias-chat-container iframe#chat-input';
const LOAD_TIMEOUT_MS = 20000;

const button = document.getElementById(BUTTON_ID);
const config = readConfig();

let widgetCss = '';
let bundleState = 'idle';
let pendingOpen = false;
let loadTimeoutTimer = null;
let isDocumentKeydownBound = false;
let hasWarmedConnection = false;
let hasInitializedCustomizations = false;
const activeInjectTimers = new Set();

if (button && config) {
  init();
}

function readConfig() {
  const element = document.getElementById(CONFIG_ID);
  if (!element) {
    return null;
  }

  try {
    const parsed = JSON.parse(element.textContent);
    return parsed && parsed.bundleSrc ? parsed : null;
  } catch (error) {
    return null;
  }
}

/**
 * Mirrors the theme's brand @font-face rules into the widget iframes by
 * reading them from the page's own stylesheets, so the declarations in
 * snippets/theme-styles-variables.liquid stay the single source of truth.
 * Protocol-relative font URLs are absolutized because the chat iframes are
 * same-origin documents whose URL is not http(s). Returns '' when the brand
 * font is not declared on the page, leaving the widget's own font alone —
 * buildWidgetCss gates the typography normalization on that same result, so
 * we never point the widget at a family the iframe cannot load.
 */
function buildBrandFontCss(fontStack) {
  const family = fontStack.split(',')[0].trim().replace(/^['"]|['"]$/g, '');
  if (!family) {
    return '';
  }

  const faces = [];
  for (const sheet of document.styleSheets) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch (error) {
      continue;
    }

    for (const rule of rules) {
      if (
        rule instanceof CSSFontFaceRule &&
        rule.style.getPropertyValue('font-family').replace(/['"]/g, '').trim() === family
      ) {
        faces.push(rule.cssText.replace(/url\((['"]?)\/\//g, 'url($1https://'));
      }
    }
  }

  if (!faces.length) {
    return '';
  }

  return faces.join('\n');
}

/**
 * Builds a data URI mask for one of the theme glyphs inlined in the
 * #ap-gorgias-icons template, so the theme's SVG assets stay the single
 * source of truth. The stroke-width custom property is baked in (the mask
 * document has no access to page variables); the mask only uses the alpha
 * channel, so the stroke color is irrelevant (currentColor resolves to
 * opaque black in a standalone SVG).
 */
function buildIconMaskUri(name, strokeWidth) {
  const template = document.getElementById(ICONS_TEMPLATE_ID);
  const svg = template && template.content.querySelector(`[data-ap-icon="${name}"] svg`);
  if (!svg) {
    return null;
  }

  const markup = svg.outerHTML.replace(/var\(--icon-stroke-width\)/g, strokeWidth);
  return `data:image/svg+xml,${encodeURIComponent(markup)}`;
}

/**
 * Swaps a Gorgias icon button's own glyph for a theme glyph, by hiding the
 * button's contents and masking the theme shape into a ::before. Masking
 * rather than injecting markup keeps this to CSS, which is all we can apply
 * to a widget whose DOM Gorgias owns and re-renders. Returns '' when the
 * glyph is missing so the widget simply keeps its native icon.
 */
function buildIconReplacementCss(selector, maskUri, size, color) {
  if (!maskUri) {
    return '';
  }

  return `
    ${selector} {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    ${selector} > * {
      display: none !important;
    }

    ${selector}::before {
      content: '';
      display: block;
      width: ${size};
      height: ${size};
      background-color: ${color};
      -webkit-mask: url("${maskUri}") center / contain no-repeat;
      mask: url("${maskUri}") center / contain no-repeat;
    }
  `;
}

function buildWidgetCss() {
  const rootStyles = getComputedStyle(document.documentElement);
  const iconSize = rootStyles.getPropertyValue('--icon-size-xs').trim() || '0.85rem';
  const smallIconSize = rootStyles.getPropertyValue('--icon-size-2xs').trim() || '0.6rem';
  const iconStrokeWidth = rootStyles.getPropertyValue('--icon-stroke-width').trim() || '1.5px';
  /* Explicit theme foreground rather than currentColor: Gorgias never sets a
     color on its icon buttons (their native icons carry their own stroke), and
     iOS Safari's UA stylesheet tints unstyled button text system blue. */
  const iconColor = rootStyles.getPropertyValue('--color-foreground').trim() || '#000';
  const backgroundColor = rootStyles.getPropertyValue('--color-background').trim() || '#fff';
  const borderColor = rootStyles.getPropertyValue('--color-border').trim() || 'rgb(0 0 0 / 0.06)';
  const inputBorderWidth = rootStyles.getPropertyValue('--style-border-width-inputs').trim() || '1px';
  const errorColor = rootStyles.getPropertyValue('--color-error').trim() || '#8b0000';
  const brandFontStack = rootStyles.getPropertyValue('--font-ap-sans--family').trim();
  const fontWeight = rootStyles.getPropertyValue('--font-ap-sans--weight').trim() || '400';
  const fontStyle = rootStyles.getPropertyValue('--font-ap-sans--style').trim() || 'normal';
  const letterSpacing = rootStyles.getPropertyValue('--letter-spacing--body-normal').trim() || '0em';
  const fontSize = rootStyles.getPropertyValue('--font-size--sm').trim() || '0.875rem';
  const faceCss = brandFontStack ? buildBrandFontCss(brandFontStack) : '';

  /* One type treatment for the whole widget: the storefront sans at a single
     weight, style, letter-spacing and colour, whatever Gorgias renders and
     wherever it renders it. Deliberately a universal selector — we are
     overriding a third-party UI we do not control, which is the one case the
     usual "no global resets" rule does not help with.

     Scoped to elements, not ::before/::after: Gorgias draws some glyphs as
     pseudo-element icon-font characters, and forcing the brand family onto
     those would render tofu. ::placeholder is likewise untouched, since black
     placeholder text reads as a filled-in value.

     Only the family is gated on the @font-face mirror actually finding the
     brand font, so a page that never declared it keeps the widget's own face
     rather than dropping to Helvetica. Weight, style, spacing and colour are
     unconditional — they normalize the widget whatever face is in use. */
  const typographyCss = `
    ${faceCss}

    * {
      ${faceCss ? `font-family: ${brandFontStack} !important;` : ''}
      font-weight: ${fontWeight} !important;
      font-style: ${fontStyle} !important;
      letter-spacing: ${letterSpacing} !important;
      color: ${iconColor} !important;
    }

    /* Size is a separate rule so it can skip the root element. Gorgias sizes
       its own paddings and layout in rem, so re-basing the root font-size
       would rescale the whole widget along with the text. Everything else
       takes one size, which is what levels the home screen's larger intro
       copy, the agent-name label and the timestamps with the messages. */
    *:not(html) {
      font-size: ${fontSize} !important;
    }
  `;

  /* The suggestion ("quick reply") row carries no id or test hook: it is the
     block in the scrolling column that holds the suggestion buttons, which
     Gorgias marks with a redundant role="button" on a real <button>. Keying
     on that rather than their aria-labels keeps the selector language-neutral. */
  const quickReplies = '[role="region"] > div:has(> span > button[role="button"])';
  const headerButtons = '#conversation-floating-header button';
  const portalButtons = '#self-service-portal-container button';
  const jumpButton = '#jump-to-last-conversation-message-floating-button';
  const closeMaskUri = buildIconMaskUri('close', iconStrokeWidth);
  const chevronMaskUri = buildIconMaskUri('chevron-left', iconStrokeWidth);
  const caretMaskUri = buildIconMaskUri('caret', iconStrokeWidth);

  return `
    ${typographyCss}

    /* Square the widget chrome. Conversation content (message bubbles etc.)
       keeps its native radii by exclusion, except interactive elements,
       which stay squared to match the storefront — the same effective rules
       the old runtime computed-style snapshot mechanism produced. */
    *:not(#conversation *),
    *::before,
    *::after {
      border-radius: 0 !important;
    }

    #conversation :is(button, input, textarea, select, option, [role="button"]),
    #conversation :is(button, input, textarea, select, option, [role="button"]) * {
      border-radius: 0 !important;
    }

    /* The email-capture form and the system alert strip live inside
       #conversation but are chrome, not conversation content, so the
       exclusion above would leave them rounded (a 20px card wrapping a 24px
       input pill, and a 16px alert). Square them explicitly — the wrappers
       are plain divs, so only their containers can be targeted. */
    form,
    form *,
    [role="alert"] {
      border-radius: 0 !important;
    }

    /* The email-capture card repeats Gorgias's chip border and drop shadow
       inside an already-bounded widget panel, which reads as a box within a
       box. Only the card itself — the field's own border is left alone,
       since that is the input affordance and it carries the error state. */
    form > div {
      border: none !important;
      box-shadow: none !important;
    }

    /* Same treatment for the message composer, which Gorgias lifts off the
       conversation with a 20px grey glow. Matched through the textarea rather
       than the wrapper's own class, which is a build hash. Deliberately not
       :has(#chat-message-input) on its own — that would also match the widget
       root and strip the panel's outer shadow against the storefront. */
    div:has(> div > #chat-message-input) {
      box-shadow: none !important;
    }

    /* Gorgias borders the composer in white at rest and only switches it to
       grey once its own data-is-focused flag flips, so the field has no
       visible edge until you click into it — it read as an edge against their
       textured panel, and reads as nothing against our white one. Give it the
       storefront's neutral border at theme input weight instead. Note this is
       --color-border, not --color-input-border: chosen deliberately to sit
       with the light edges Gorgias uses on the pills around it rather than
       the storefront's heavier black input rule. The radius is set here too
       because the bordered element is a plain wrapper div, so the
       #conversation interactive-elements rule does not reach it and it keeps
       a 24px pill in the conversation view. Unscoped by design — the same
       composer serves the home screen, where it sits outside #conversation. */
    div:has(> div > #chat-message-input) {
      border: ${inputBorderWidth} solid ${borderColor} !important;
      border-radius: 0 !important;
    }

    /* Their focus affordance was that border recolour, which the rule above
       overrides — so restore one. A faint resting edge cannot also carry
       focus, so this thickens to the full foreground colour rather than
       merely doubling the border, keeping the indicator clearly visible. */
    div:has(> div > #chat-message-input):focus-within {
      box-shadow: 0 0 0 ${inputBorderWidth} ${iconColor} !important;
    }

    /* The composer strip is only 50% white and its input box 90%, so message
       text scrolling underneath stays legible straight through the panel
       chrome. Make the whole stack between the strip and the textarea opaque
       so messages disappear cleanly behind it. Matched through the textarea
       rather than by depth — the wrappers are all build hashes — and every
       match is confined to that strip. */
    #conversation div:has(#chat-message-input) {
      background-color: ${backgroundColor} !important;
    }

    /* Message bubbles. Outbound messages are filled with a linear-gradient
       (not a background-color, so it survives colour overrides) and rounded;
       inbound ones are already bare. Matching every message wrapper keeps the
       two sides identical whichever way Gorgias renders them. */
    #conversation div:has(> p) {
      background-color: transparent !important;
      background-image: none !important;
      border-radius: 0 !important;
    }

    /* Gorgias's own product palette (navies, a blue info state, a coral
       error state) leaks into the widget. Text is already handled by the
       typography rule above; these are the surfaces it cannot reach.

       The submit button is the one carve-out from black text: it is the only
       control that sits on a dark fill, so it takes the background colour to
       stay legible. */
    form button[type="submit"] {
      background-color: ${iconColor} !important;
      color: ${backgroundColor} !important;
    }

    [role="alert"] {
      border-color: ${borderColor} !important;
    }

    /* Validation is the one deliberate exception to the all-black typography
       rule above: the error text, the required marker and the invalid field's
       border all carry the storefront's error colour, so the state reads at a
       glance rather than only on close reading. These selectors outrank the
       universal rule on specificity, which is what lets them through. */
    form :has(> input[aria-invalid="true"]) {
      border-color: ${errorColor} !important;
    }

    form label span,
    form [id$="-caption"] {
      color: ${errorColor} !important;
    }

    /* Keep the widget on the storefront background in every state. Gorgias
       lays decorative noise-texture overlays over its own base, which read as
       light gray wherever no pane covers them (header band, home screen). The
       full-size one sits as the widget root's first child; the rest are tagged
       by JS (tagNoiseLayers) from their computed background-image, since their
       wrappers have no stable structure. */
    div[class*="widget-"] {
      background-color: ${backgroundColor} !important;
    }

    div[class*="widget-"] > div:first-child,
    [data-ap-gorgias-noise] {
      display: none !important;
    }

    /* The texture that actually tints the panel grey is painted onto two
       1600x1600 <canvas> layers inside the widget root — pixels in a bitmap,
       not a CSS background. Nothing above reaches it: the elements report no
       background, and being pointer-events:none they do not even show up in
       elementsFromPoint. Only hiding the canvas removes it.

       Deliberately every canvas in the widget: the decorative layers carry no
       attribute that distinguishes them, and Gorgias chat renders nothing
       functional on a canvas today (attachments are <img>). If that ever
       changes, narrow this rather than dropping it. */
    div[class*="widget-"] canvas {
      display: none !important;
    }

    /* The home screen lays a full-size light-grey gradient pane over the
       widget root, so forcing the root's background colour is not enough —
       the pane paints a background-image, which no colour override reaches.
       The floating header is excluded: its white gradient is a functional
       scrim that keeps the back and close controls legible over scrolling
       content, not decoration. */
    #self-service-portal-container
      div:not(:is(#conversation-floating-header, #conversation-floating-header *)) {
      background-image: none !important;
    }

    /* Gorgias focuses its root widget div when the chat opens, which draws
       a white outline offset from the widget edge. Scoped to divs so
       button/input focus indicators inside the chat are unaffected. */
    div[class*="widget-"]:focus,
    div[class*="widget-"]:focus-visible {
      outline: none !important;
    }

    /* Gorgias renders the privacy-policy disclaimer and the suggestion row
       outside the padded message column, so both sit hard against the widget
       edge (the disclaimer's text runs to within 1px of it). Restore the 20px
       inset the message column itself uses. This is a defect in the Gorgias
       build, not something our CSS causes — verified by measuring with this
       stylesheet disabled — so drop these two rules if Gorgias fixes it. */
    [role="note"]:has(> #privacy-policy-title) {
      padding-inline: 20px !important;
    }

    ${quickReplies} {
      padding-bottom: 20px !important;
    }

    /* The home screen centres its intro heading against a left-aligned
       storefront. "start" rather than "left" so it still flips with the
       writing mode, and it matches what the surrounding columns already
       compute to. Set on the header block, which inherits down to the copy. */
    #homepage-header {
      text-align: start !important;
    }

    /* Gorgias butts the agent's avatar/name row straight up against their
       first message (0px), while consecutive messages get 4px. The avatar is
       taller than the name text, so it lands 8px from the message where a
       customer message sits 12px from its neighbour, and the agent's turn
       reads cramped. 4px here levels the two at 12px. Keyed on the <h3> the
       row wraps, which is the only non-hashed hook it has. */
    #conversation div:has(> h3) {
      margin-bottom: 4px !important;
    }

    /* Drop the drop shadow Gorgias puts on its chip-style controls. Excluded
       on :focus-visible in case a build ever expresses the focus ring as a
       shadow — losing that would take the focus indicator with it. */
    ${quickReplies} button:not(:focus-visible),
    ${portalButtons}:not(:focus-visible),
    ${headerButtons}:not(:focus-visible) {
      box-shadow: none !important;
    }

    /* The header's back and close controls are icon-only, so the chip border
       reads as chrome around the glyph rather than as a button. */
    ${headerButtons} {
      border: none !important;
    }

    /* Swap Gorgias's own glyphs for the theme icons. Sizes match the
       storefront drawer controls (base.css .close-button svg). */
    ${buildIconReplacementCss(`${headerButtons}[aria-label*="Close" i]`, closeMaskUri, iconSize, iconColor)}

    /* Any header control that is not the close button is the back/previous
       affordance, which only mounts in the conversation view. */
    ${buildIconReplacementCss(`${headerButtons}:not([aria-label*="Close" i])`, chevronMaskUri, iconSize, iconColor)}

    /* Inline dismiss on the system alert strip — a 16px control, so it takes
       the smaller theme icon size. */
    ${buildIconReplacementCss('[role="alert"] button[aria-label*="Close" i]', closeMaskUri, smallIconSize, iconColor)}

    /* Scroll-to-latest control, which floats over the conversation when the
       visitor has scrolled up. The theme's caret is the storefront's own
       down-chevron (selects, accordions). Its chip border and shadow are
       deliberately left in place: unlike the header controls it sits over
       message content, where that edge is what separates it from the text
       behind it. */
    ${buildIconReplacementCss(jumpButton, caretMaskUri, iconSize, iconColor)}
  `;
}

function init() {
  button.hidden = false;
  button.addEventListener('click', onButtonClick);
  button.addEventListener('pointerenter', warmConnection, { once: true });
  button.addEventListener('focus', warmConnection, { once: true });

  window.addEventListener('gorgias-widget-loaded', onWidgetLoaded, { once: true });

  /* Gorgias already installed by other means (e.g. the app embed enabled
     alongside this integration). The object may be a pre-init stub, so wait
     for init() to resolve before wiring customizations — calling
     onWidgetLoaded against the stub would silently skip the event listeners
     and observers (the initialization guard latches). */
  if (window.GorgiasChat && typeof window.GorgiasChat.init === 'function') {
    bundleState = 'loading';
    window.GorgiasChat.init().then(onWidgetLoaded).catch(function () {
      resetToIdle();
    });
  }
}

function warmConnection() {
  if (hasWarmedConnection) {
    return;
  }

  hasWarmedConnection = true;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = new URL(config.bundleSrc).origin;
  document.head.appendChild(link);
}

function onButtonClick() {
  if (bundleState === 'ready') {
    if (!window.GorgiasChat || typeof window.GorgiasChat.open !== 'function' || typeof window.GorgiasChat.close !== 'function') {
      return;
    }

    if (isGorgiasChatOpen()) {
      closeGorgiasChat();
    } else {
      openGorgiasChat();
    }
    return;
  }

  pendingOpen = true;

  if (bundleState === 'idle') {
    injectBundle();
  }
}

function injectBundle() {
  bundleState = 'loading';
  setLoadingState(true);

  /* A previous attempt may have timed out with its script still in flight
     (resetToIdle leaves it in place). Re-arm the timeout and let the armed
     gorgias-widget-loaded listener finish the job rather than injecting a
     duplicate bundle. Only a script that errored is removed (below), so a
     fresh injection only ever happens after a confirmed failure. */
  if (!document.getElementById(BUNDLE_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = BUNDLE_SCRIPT_ID;
    script.src = config.bundleSrc;
    script.addEventListener('error', function () {
      script.remove();
      resetToIdle();
    });
    document.head.appendChild(script);
  }

  loadTimeoutTimer = window.setTimeout(resetToIdle, LOAD_TIMEOUT_MS);
}

function resetToIdle() {
  if (bundleState !== 'loading') {
    return;
  }

  bundleState = 'idle';
  pendingOpen = false;
  setLoadingState(false);
  clearLoadTimeout();
}

function clearLoadTimeout() {
  if (loadTimeoutTimer !== null) {
    window.clearTimeout(loadTimeoutTimer);
    loadTimeoutTimer = null;
  }
}

function setLoadingState(isLoading) {
  if (isLoading) {
    button.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');
  } else {
    delete button.dataset.state;
    button.removeAttribute('aria-busy');
  }
}

function onWidgetLoaded() {
  bundleState = 'ready';
  setLoadingState(false);
  clearLoadTimeout();
  initGorgiasCustomizations();

  if (pendingOpen) {
    pendingOpen = false;
    openGorgiasChat();
  }
}

function openGorgiasChat() {
  if (!window.GorgiasChat || typeof window.GorgiasChat.open !== 'function') {
    return;
  }

  bindGorgiasDocumentKeydown();

  try {
    window.GorgiasChat.open();
    updateButtonExpanded(true);
  } catch (error) {
    unbindGorgiasDocumentKeydown();
    updateButtonExpanded(false);
  }
}

function closeGorgiasChat(options) {
  if (window.GorgiasChat && typeof window.GorgiasChat.close === 'function' && isGorgiasChatOpen()) {
    window.GorgiasChat.close();
  }

  unbindGorgiasDocumentKeydown();
  updateButtonExpanded(false);

  if (options && options.returnFocus) {
    button.focus();
  }
}

function bindGorgiasDocumentKeydown() {
  if (isDocumentKeydownBound) {
    return;
  }

  document.addEventListener('keydown', onGorgiasDocumentKeyDown, true);
  isDocumentKeydownBound = true;
}

function unbindGorgiasDocumentKeydown() {
  if (!isDocumentKeydownBound) {
    return;
  }

  document.removeEventListener('keydown', onGorgiasDocumentKeyDown, true);
  isDocumentKeydownBound = false;
}

function onGorgiasDocumentKeyDown(event) {
  if (event.key !== 'Escape' && event.code !== 'Escape') {
    return;
  }

  if (!isGorgiasChatOpen()) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  closeGorgiasChat({ returnFocus: true });
}

function isGorgiasChatWindowVisible() {
  const chatWindow = document.querySelector(CHAT_WINDOW_SELECTOR);

  if (!chatWindow) {
    return false;
  }

  const styles = window.getComputedStyle(chatWindow);
  const rect = chatWindow.getBoundingClientRect();

  return styles.display !== 'none'
    && styles.visibility !== 'hidden'
    && Number(styles.opacity) !== 0
    && rect.width > 0
    && rect.height > 0;
}

function isGorgiasChatOpen() {
  return Boolean(
    window.GorgiasChat
    && (
      (typeof window.GorgiasChat.isOpen === 'function' && window.GorgiasChat.isOpen())
      || isGorgiasChatWindowVisible()
    )
  );
}

function isGorgiasChatTrigger(target) {
  return Boolean(button.contains(target));
}

function isGorgiasChatIframe(target) {
  return Boolean(
    target.closest &&
    target.closest('#gorgias-chat-container iframe:is(#chat-window, #chat-input, #chat-button)')
  );
}

function closeGorgiasChatOnOutsideClick(event) {
  if (!window.GorgiasChat || !isGorgiasChatOpen()) {
    return;
  }

  if (isGorgiasChatTrigger(event.target) || isGorgiasChatIframe(event.target)) {
    return;
  }

  closeGorgiasChat();
}

function updateButtonExpanded(isExpanded) {
  button.setAttribute('aria-expanded', String(Boolean(isExpanded)));
}

function bindChatWindowClickAway(iframe) {
  const iframeDocument = getIframeDocument(iframe);
  if (!iframeDocument || iframe.apGorgiasClickAwayDocument === iframeDocument) {
    return false;
  }

  iframe.apGorgiasClickAwayDocument = iframeDocument;
  iframeDocument.addEventListener('click', function (event) {
    if (!window.GorgiasChat || !isGorgiasChatOpen()) {
      return;
    }

    /* Anchored on the widget root rather than #conversation. Gorgias renders
       the floating header — and the back control in it — as a sibling of
       #conversation, not a child, so keying off #conversation treated Back as
       a click on the backdrop and closed the whole widget. The root covers the
       full panel, leaving only the iframe's transparent shadow gutter outside
       it, which is the backdrop this handler is actually meant to catch. */
    const widgetRoot = iframeDocument.querySelector('div[class*="widget-"]');
    if (!widgetRoot || widgetRoot.contains(event.target)) {
      return;
    }

    closeGorgiasChat();
  }, true);

  return true;
}

function getIframeHead(iframe) {
  try {
    return iframe && iframe.contentWindow && iframe.contentWindow.document.querySelector('head');
  } catch (error) {
    return null;
  }
}

function getIframeDocument(iframe) {
  try {
    return iframe && iframe.contentWindow && iframe.contentWindow.document;
  } catch (error) {
    return null;
  }
}

function isGorgiasIframeReady(head) {
  return head && Array.from(head.children).some(function (element) {
    return element.getAttribute('data-emotion') !== null;
  });
}

function injectStyleIntoIframe(iframeSelector, styleId, cssText, onStyleReady) {
  if (activeInjectTimers.has(styleId)) {
    return;
  }

  activeInjectTimers.add(styleId);

  let attempts = 0;
  const timer = window.setInterval(function () {
    attempts += 1;
    const iframe = document.querySelector(iframeSelector);
    const iframeHead = getIframeHead(iframe);

    if (!iframeHead) {
      if (attempts >= 200) {
        window.clearInterval(timer);
        activeInjectTimers.delete(styleId);
      }
      return;
    }

    if (!isGorgiasIframeReady(iframeHead) && attempts < 200) {
      return;
    }

    window.clearInterval(timer);
    activeInjectTimers.delete(styleId);

    let style = iframeHead.querySelector('#' + styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
    }

    if (typeof onStyleReady === 'function') {
      onStyleReady(iframe, style);
    }

    style.textContent = cssText;

    if (!style.parentNode) {
      iframeHead.appendChild(style);
    }
  }, 50);
}

/**
 * Tags the widget's decorative noise-texture layers so the injected CSS can
 * hide them. They are identified by computed style because their wrapper
 * divs carry only build-hash classes and their DOM position varies between
 * widget states (anchoring on structure hid the whole header band in the
 * pre-conversation state). Scoped to a subtree so the mutation watcher only
 * pays for the nodes that actually arrived.
 */
function tagNoiseLayers(root, view) {
  if (!root || root.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const candidates = [...root.querySelectorAll('div:not([data-ap-gorgias-noise])')];
  if (root.matches('div:not([data-ap-gorgias-noise])')) {
    candidates.push(root);
  }

  candidates.forEach(function (element) {
    const backgroundImage = view.getComputedStyle(element).backgroundImage;
    if (backgroundImage && backgroundImage.includes('noise')) {
      element.setAttribute('data-ap-gorgias-noise', '');
    }
  });
}

/**
 * Keeps the noise-layer tagging current for the life of the iframe. Gorgias
 * mounts panes lazily (the composer and its surrounds only arrive when the
 * chat is online), so a single pass at injection time leaves later layers
 * showing through as gray. Only added subtrees are re-scanned, and the
 * observer is stored on the iframe so a re-created iframe gets a fresh one
 * rather than a second observer on the same document.
 */
function watchNoiseLayers(iframe) {
  const iframeDocument = getIframeDocument(iframe);
  /* Bail rather than throw on a body-less document: this runs as
     injectStyleIntoIframe's onStyleReady callback, before the stylesheet's
     text is set, so an exception here would cost the widget all its CSS. */
  if (!iframeDocument || !iframeDocument.body || iframe.apGorgiasNoiseObserver) {
    return;
  }

  const view = iframe.contentWindow;
  tagNoiseLayers(iframeDocument.body, view);

  const observer = new (view.MutationObserver || window.MutationObserver)(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        tagNoiseLayers(node, view);
      });
    });
  });

  observer.observe(iframeDocument.body, { childList: true, subtree: true });
  iframe.apGorgiasNoiseObserver = observer;
}

function prepareChatWindow(iframe) {
  bindChatWindowClickAway(iframe);
  watchNoiseLayers(iframe);
}

function squareGorgiasWidget() {
  /* Built lazily: this is the first point any widget CSS is needed, and it
     can only be reached after the visitor clicks the launcher — keeping the
     getComputedStyle reads and string build off the page-load path. */
  if (!widgetCss) {
    widgetCss = buildWidgetCss();
  }

  injectStyleIntoIframe(
    CHAT_WINDOW_SELECTOR,
    'ap-gorgias-square-chat-window',
    widgetCss,
    prepareChatWindow
  );
  /* #chat-input is a second iframe the 2026-07 build creates but leaves empty
     and display:none — the live composer is #chat-message-input, inside
     #chat-window. Styled anyway so it is covered if Gorgias ever mounts the
     composer there; it carries no conversation, so it takes the noise watcher
     but not the click-away binding. */
  injectStyleIntoIframe(
    CHAT_INPUT_SELECTOR,
    'ap-gorgias-square-chat-input',
    widgetCss,
    watchNoiseLayers
  );
}

function initGorgiasCustomizations() {
  if (hasInitializedCustomizations) {
    return;
  }

  hasInitializedCustomizations = true;

  squareGorgiasWidget();
  updateButtonExpanded(isGorgiasChatOpen());
  document.addEventListener('click', closeGorgiasChatOnOutsideClick, true);

  if (window.GorgiasChat && typeof window.GorgiasChat.on === 'function') {
    window.GorgiasChat.on('widget:opened', function () {
      bindGorgiasDocumentKeydown();
      updateButtonExpanded(true);
      squareGorgiasWidget();
    });
    window.GorgiasChat.on('widget:closed', function () {
      unbindGorgiasDocumentKeydown();
      updateButtonExpanded(false);
    });
  }

  const chatContainer = document.getElementById('gorgias-chat-container');
  if (chatContainer) {
    const observer = new MutationObserver(function () {
      squareGorgiasWidget();
    });
    observer.observe(chatContainer, { childList: true, subtree: true });
  }
}
